import React, { FC, useEffect, useState } from 'react';

import { Breadcrumbs, Chip } from '@material-ui/core';
import Delete from '@material-ui/icons/Delete';
import { useMutation, useQuery } from 'react-apollo';
import { Link, useHistory } from 'react-router-dom';

import Button from 'Components-redesign/Button';
import ImageSelect from 'Components-redesign/Fields/ImageSelect';
import { Input } from 'Components-redesign/Fields/New/Input';
import Textarea from 'Components-redesign/Fields/Textarea';
import Field from 'Components-redesign/Form/Field';
import Loader from 'Components-redesign/Loader';
import Modal from 'Components-redesign/Modal';
import ModalContentWrapper from 'Components-redesign/Modal/ModalContentWrapper';
import useModal from 'Components-redesign/Modal/useModal';
import { Heading } from 'Components-redesign/Typography/Heading';
import { Paragraph } from 'Components-redesign/Typography/Paragraph';
import { useI18n } from 'Hooks';
import {
  AiQuery,
  AiQueryVariables,
  CreateRelease,
  DeleteRelease,
  GetRelease,
  UpdateRelease,
} from 'Scenes/Releases/api/__queries__';
import {
  AI_HELP,
  CREATE_RELEASE,
  DELETE_RELEASE,
  UPDATE_RELEASE,
} from 'Scenes/Releases/api/mutations';
import { GET_RELEASE } from 'Scenes/Releases/api/queries';
import { AI_CONTEXT } from 'Scenes/Releases/consts';
import { RELEASE_TYPE } from 'Scenes/Releases/types';
import { Box } from 'styles/box';

import { DeleteWrapper, ScrollableBox, Wrapper } from '../../styles';

interface FormData {
  createdBy: string;
  titleLang: {
    en: string;
    se: string;
    es: string;
    no: string;
    tr: string;
    da: string;
    ar: string;
  };
  bodyLang: {
    en: string;
    se: string;
    es: string;
    no: string;
    tr: string;
    da: string;
    ar: string;
  };
  imageUrls: string[];
  productTags: string[];
}

interface Props {
  id?: string;
  username: string;
  refetch: () => void;
  onComplete: () => void;
}

/** Create & Edit a release with type FEATURE */
const FeatureForm: FC<Props> = ({ id, username, refetch, onComplete }) => {
  const { t } = useI18n();

  const getLanguageName = (langCode: string): string => {
    const languageMap: Record<string, string> = {
      en: t('Companies.Form.Languages.English'),
      se: t('Companies.Form.Languages.Swedish'),
      es: t('Companies.Form.Languages.Spanish'),
      no: t('Companies.Form.Languages.Norwegian'),
      tr: t('Companies.Form.Languages.Turkish'),
      da: t('Companies.Form.Languages.Danish'),
      ar: t('Companies.Form.Languages.Arabic'),
    };

    return languageMap[langCode] || langCode;
  };

  const titleTranslationModal = useModal();
  const bodyTranslationModal = useModal();
  const confirmDeleteModal = useModal();
  const history = useHistory();

  const [createRelease] = useMutation<CreateRelease>(CREATE_RELEASE);
  const [updateRelease] = useMutation<UpdateRelease>(UPDATE_RELEASE);
  const [deleteRelease] = useMutation<DeleteRelease>(DELETE_RELEASE);
  const [aiHelper] = useMutation<AiQuery, AiQueryVariables>(AI_HELP);

  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingTitleLang, setLoadingTitleLang] = useState<boolean>(false);
  const [loadingBodyLang, setLoadingBodyLang] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormData>({
    createdBy: username,
    titleLang: {
      en: '',
      se: '',
      es: '',
      no: '',
      tr: '',
      da: '',
      ar: '',
    },
    bodyLang: {
      en: '',
      se: '',
      es: '',
      no: '',
      tr: '',
      da: '',
      ar: '',
    },
    imageUrls: [],
    productTags: [],
  });

  const releaseQuery = useQuery<GetRelease>(GET_RELEASE, {
    variables: {
      id,
    },
    skip: !id || id === 'new',
  });

  const release = releaseQuery.data?.getRelease;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (id && id !== 'new') {
      /** Update */
      await updateRelease({
        variables: {
          input: {
            id,
            type: RELEASE_TYPE.FEATURE,
            titleLang: formData.titleLang,
            bodyLang: formData.bodyLang,
            imageUrls: formData.imageUrls,
            productTags: formData.productTags,
          },
        },
      });
    } else {
      /** Create */
      try {
        await createRelease({
          variables: {
            input: {
              createdBy: username,
              type: RELEASE_TYPE.FEATURE,
              titleLang: formData.titleLang,
              bodyLang: formData.bodyLang,
              imageUrls: formData.imageUrls,
              productTags: formData.productTags,
            },
          },
        });
      } catch (error) {
        console.error('Error creating release:', error);
        setError(error);
      }
    }

    refetch();
    history.push('/releases');
    onComplete();
    setLoading(false);
  };

  const handleDelete = async () => {
    confirmDeleteModal.hide();

    setDeleteLoading(true);

    try {
      await deleteRelease({
        variables: {
          id,
        },
      });

      refetch();
      history.push('/releases');
    } catch (error) {
      console.error('Error deleting release:', error);
      setError(error);
    }

    setDeleteLoading(false);
  };

  useEffect(() => {
    if (release) {
      setFormData({
        titleLang: release.titleLang,
        bodyLang: release.bodyLang,
        imageUrls: release.imageUrls,
        productTags: release.productTags,
        createdBy: release.createdBy,
      });
    }
  }, [release]);

  const handleGenerateLang = async (field?: 'TITLE' | 'BODY') => {
    if (field === 'TITLE') {
      setLoadingTitleLang(true);
    }

    if (field === 'BODY') {
      setLoadingBodyLang(true);
    }

    try {
      const res = await aiHelper({
        variables: {
          context: AI_CONTEXT,
          input: field === 'TITLE' ? formData.titleLang.en : formData.bodyLang.en,
        },
      });

      const aiMessage = res.data?.generateCompletion.message;

      if (!aiMessage) {
        setError('AI response is empty or invalid');
        return;
      }

      const parsedMessage = JSON.parse(aiMessage);

      if (res.data) {
        if (field === 'TITLE') {
          setFormData((prevData) => ({
            ...prevData,
            titleLang: {
              ...prevData.titleLang,
              se: parsedMessage.se,
              es: parsedMessage.es,
              no: parsedMessage.no,
              tr: parsedMessage.tr,
              da: parsedMessage.da,
              ar: parsedMessage.ar,
            },
          }));
        } else {
          setFormData((prevData) => ({
            ...prevData,
            bodyLang: {
              ...prevData.bodyLang,
              se: parsedMessage.se,
              es: parsedMessage.es,
              no: parsedMessage.no,
              tr: parsedMessage.tr,
              da: parsedMessage.da,
              ar: parsedMessage.ar,
            },
          }));
        }
      }
    } catch (error) {
      console.error('Error generating translations:', error);
      setError(error);
    } finally {
      if (field === 'TITLE') {
        setLoadingTitleLang(false);
      }

      if (field === 'BODY') {
        setLoadingBodyLang(false);
      }
    }
  };

  const isTitleLangGenerated = Object.values(formData.titleLang).every((value) => value.trim());
  const isBodyLangGenerated = Object.values(formData.bodyLang).every((value) => value.trim());

  return (
    <>
      <Wrapper loading={deleteLoading}>
        <Breadcrumbs style={{ marginBottom: '8px' }} aria-label="breadcrumb">
          <Link to="/releases">{t('Releases.Breadcrumb')}</Link>
          <p>{release ? `"${t(release.titleLang)}"` : t('Releases.PublishNewFeature')}</p>
        </Breadcrumbs>

        {releaseQuery.loading ? (
          <Box
            display="flex"
            marginTop="xl"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gap="small">
            <Loader color="var(--colorBlack)" />
            <Paragraph level={3}>{t('Releases.FeatureForm.Loading')}</Paragraph>
          </Box>
        ) : (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Heading level={1}>
                  {release
                    ? `${t('General.Edit')} ${t(release.titleLang)}`
                    : t('Releases.FeatureForm.PublishNew')}
                </Heading>
                {!release && <Paragraph level={3}>{t('Releases.FeatureForm.Desc')}</Paragraph>}
              </Box>

              {release && (
                <DeleteWrapper onClick={confirmDeleteModal.toggle}>
                  <Delete fontSize="large" htmlColor="var(--colorBlueGray900)" />
                </DeleteWrapper>
              )}
            </Box>

            <form onSubmit={handleSubmit}>
              <Box display="flex" flexDirection="column" gap="small">
                <Field label={t('Releases.FeatureForm.TitleLang')}>
                  <Input
                    type="text"
                    required
                    value={formData.titleLang.en}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        titleLang: { ...formData.titleLang, en: e.target.value },
                      })
                    }
                    placeholder={t('Releases.FeatureForm.TitleLang.Placeholder')}
                  />
                  <Button
                    type="button"
                    text={
                      loadingTitleLang
                        ? `${t('General.Loading')}...`
                        : isTitleLangGenerated
                        ? t('Releases.ViewEditLang')
                        : t('Releases.GenerateLang')
                    }
                    styleType={isTitleLangGenerated ? 'Dark' : 'OK'}
                    disabled={loadingTitleLang || !formData.titleLang.en.trim()}
                    handleClick={() =>
                      isTitleLangGenerated
                        ? titleTranslationModal.toggle()
                        : handleGenerateLang('TITLE')
                    }
                    height="40px"
                    style={{ marginTop: '8px' }}
                  />
                </Field>

                <Field label={t('Releases.FeatureForm.Body')}>
                  <Textarea
                    name="bodyLang"
                    resizableVertically
                    scrollable
                    placeHolder={t('Releases.FeatureForm.Body.Desc')}
                    value={formData.bodyLang.en}
                    handleChange={(e) =>
                      setFormData({
                        ...formData,
                        bodyLang: { ...formData.bodyLang, en: e.target.value },
                      })
                    }
                  />
                  <Button
                    type="button"
                    text={
                      loadingBodyLang
                        ? `${t('General.Loading')}...`
                        : isBodyLangGenerated
                        ? t('Releases.ViewEditLang')
                        : t('Releases.GenerateLang')
                    }
                    styleType={isBodyLangGenerated ? 'Dark' : 'OK'}
                    disabled={loadingBodyLang || !formData.bodyLang.en.trim()}
                    handleClick={() =>
                      isBodyLangGenerated
                        ? bodyTranslationModal.toggle()
                        : handleGenerateLang('BODY')
                    }
                    height="40px"
                    style={{ marginTop: '8px' }}
                  />
                </Field>

                <Field
                  label={t('Releases.FeatureForm.ImageUrls')}
                  description={t('Releases.FeatureForm.ImageUrls.Desc')}>
                  {/* Render existing images */}
                  {formData.imageUrls.length > 0 && (
                    <Box display="flex" gap="xs" marginBottom="small">
                      {formData.imageUrls.map((url, index) => (
                        <Box
                          key={`image-${index}`}
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          gap="xs">
                          <img src={url} alt={`Uploaded ${index}`} style={{ height: '100px' }} />
                          <Button
                            styleType="Warning"
                            height="24px"
                            width="80px"
                            text={t('General.Remove')}
                            type="button"
                            handleClick={() => {
                              setFormData((prevData) => ({
                                ...prevData,
                                imageUrls: prevData.imageUrls.filter((_, i) => i !== index),
                              }));
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Always have one empty ImageSelect for adding new images */}
                  <ImageSelect
                    key={`imageUrl-${formData.imageUrls.length}`}
                    name={`imageUrl-${formData.imageUrls.length}`}
                    title={t('Releases.FeatureForm.ImageUrls.Add')}
                    setImgIdForAdding={(name, value, url) => {
                      if (url) {
                        setFormData((prevData) => ({
                          ...prevData,
                          imageUrls: [...prevData.imageUrls, url],
                        }));
                      }
                    }}
                  />
                </Field>

                <Field
                  label={t('Releases.FeatureForm.ProductTags')}
                  description={t('Releases.FeatureForm.ProductTags.Desc')}>
                  <Box display="flex" flexDirection="column" gap="small">
                    <Box display="flex" gap="xs" marginTop="xs">
                      {formData.productTags.map((tag, index) => (
                        <Chip
                          key={tag + index}
                          label={tag}
                          onDelete={() => {
                            setFormData((prevData) => ({
                              ...prevData,
                              productTags: prevData.productTags.filter((_, i) => i !== index),
                            }));
                          }}
                        />
                      ))}
                    </Box>
                    <Input
                      type="text"
                      placeholder={t('Releases.FeatureForm.ProductTags.Placeholder')}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.target as HTMLInputElement).value.trim()) {
                          e.preventDefault(); // Prevent form submission
                          const newTag = (e.target as HTMLInputElement).value.trim();
                          if (!formData.productTags.includes(newTag)) {
                            setFormData((prevData) => ({
                              ...prevData,
                              productTags: [...prevData.productTags, newTag],
                            }));
                          }
                          (e.target as HTMLInputElement).value = ''; // Clear the input field
                        }
                      }}
                    />
                  </Box>
                </Field>

                <Box
                  display="flex"
                  flexDirection="column"
                  gap="xs"
                  marginTop="medium"
                  alignItems="center">
                  <Button
                    type="submit"
                    text={
                      loading
                        ? `${t('General.Loading')}...`
                        : release
                        ? t('General.Update')
                        : t('Releases.Publish')
                    }
                    styleType="OK"
                    disabled={loading || !isTitleLangGenerated || !isBodyLangGenerated}
                  />
                  {(!isTitleLangGenerated || !isBodyLangGenerated) && (
                    <Paragraph level={4} color="var(--colorBlueGray900)">
                      {t('Releases.FeatureForm.TranslationsNotGenerated')}
                    </Paragraph>
                  )}
                </Box>

                {error && (
                  <Paragraph level={4} color="var(--colorUIRed)">
                    {error}
                  </Paragraph>
                )}
              </Box>
            </form>
          </>
        )}
      </Wrapper>

      <Modal isShowing={titleTranslationModal.isShowing} hide={titleTranslationModal.hide}>
        <ModalContentWrapper>
          <Box display="flex" flexDirection="column" gap="large">
            <Box display="flex" flexDirection="column" gap="large">
              <Heading level={3}>{t('Releases.FeatureForm.TitleLang.Translations')}</Heading>
            </Box>

            <ScrollableBox display="flex" flexDirection="column" gap="small">
              {Object.entries(formData.titleLang).map(([lang, text]) => (
                <Field
                  label={`${t('Releases.FeatureForm.TitleLang')} - ${getLanguageName(lang)}`}
                  key={lang}>
                  <Input
                    key={lang}
                    name={`titleLang-${lang}`}
                    placeholder={t('Releases.FeatureForm.TitleLang.Placeholder')}
                    value={text}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        titleLang: { ...formData.titleLang, [lang]: e.target.value },
                      })
                    }
                  />
                </Field>
              ))}
            </ScrollableBox>
          </Box>
        </ModalContentWrapper>
      </Modal>

      <Modal isShowing={bodyTranslationModal.isShowing} hide={bodyTranslationModal.hide}>
        <ModalContentWrapper>
          <Box display="flex" flexDirection="column" gap="large">
            <Box display="flex" flexDirection="column" gap="large">
              <Heading level={3}>{t('Releases.FeatureForm.Body.Translations')}</Heading>
            </Box>

            <ScrollableBox display="flex" flexDirection="column" gap="small">
              {Object.entries(formData.bodyLang).map(([lang, text]) => (
                <Field
                  label={`${t('Releases.FeatureForm.Body')} - ${getLanguageName(lang)}`}
                  key={lang}>
                  <Textarea
                    key={lang}
                    name={`bodyLang-${lang}`}
                    resizableVertically
                    scrollable
                    placeHolder={t('Releases.FeatureForm.Body.Desc')}
                    value={text}
                    handleChange={(e) =>
                      setFormData({
                        ...formData,
                        bodyLang: { ...formData.bodyLang, [lang]: e.target.value },
                      })
                    }
                  />
                </Field>
              ))}
            </ScrollableBox>
          </Box>
        </ModalContentWrapper>
      </Modal>

      <Modal isShowing={confirmDeleteModal.isShowing} hide={confirmDeleteModal.hide}>
        <ModalContentWrapper>
          <Box display="flex" flexDirection="column" gap="large">
            <Heading level={3}>{t('Releases.FeatureForm.ConfirmDelete')}</Heading>

            <Box display="flex" alignItems="center" gap="xs">
              <Button
                styleType="Dark"
                text={t('General.Cancel')}
                handleClick={confirmDeleteModal.hide}
              />
              <Button styleType="Warning" text={t('General.Delete')} handleClick={handleDelete} />
            </Box>
          </Box>
        </ModalContentWrapper>
      </Modal>
    </>
  );
};

export default FeatureForm;
